import { FC } from "react";
import { useLocation } from "react-router-dom";
import Logo from "../../components/Logo/Logo";
import { icons } from "../../icons/IconSet";
import useTicket from "../../hooks/useTicket";
import useOrder from "../../hooks/useOrder";
import { isPublicPath } from "../../routing/publicPaths";

const Header: FC =() => {
  const { handleExit } = useTicket();
  const { isOrderAuthInfo } = useOrder();
  const { pathname } = useLocation();

  // --
  // -- Public pages (the PartsCare offer) have no order session to exit, and the
  // -- visitor may have arrived straight from an emailed link. Signing them out
  // -- and bouncing them to /ordersignin is never what they meant, so offer Back.
  // --
  const isPublic = isPublicPath(pathname);

  // react-router tracks its position in the history stack; index 0 means this page
  // is where the visitor entered, so there is nothing in-app to go back to.
  const canGoBack = ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0;

  // Stored credentials mean we can land them on their order details, which beats
  // browser history: it works even when they opened the link straight from email.
  const hasOrderSession = isOrderAuthInfo();

  const showBack = isPublic && (hasOrderSession || canGoBack);
  // No session and nothing to go back to: offer nothing rather than a dead control.
  const hideAction = isPublic && !showBack;

  // --
  // -- A full document load, not navigate(): App reads isPublicPath once at mount
  // -- and skips the order bootstrap on public pages, so a client-side route change
  // -- would land on /order with no order loaded and render a blank page.
  // -- Reloading remounts App, which then fetches the order properly - and if the
  // -- stored credentials are stale, the usual 404 redirect to sign-in still fires.
  // --
  const goBack = () => {
    if (hasOrderSession) window.location.href = `${process.env.REACT_APP_BASE_URL}order`;
    else window.history.back();
  };

  return (
    <div className="flex mx-auto w-full lg:w-max-[1100px] border-border_color border-b-[1px] pb-4 sm:pb-6 justify-between">
      <Logo />
      {hideAction ? null : (
        <div
          tabIndex={-1}
          role="button"
          onClick={showBack ? goBack : handleExit}
          className="trans transition-all hover:text-primary p-2 flex gap-[5px] items-center text-md"
        >
          {showBack ? (
            <>
              {/* No back arrow in the icon set; 'forward' rotated does the job. */}
              <span className="inline-flex rotate-180">{icons('forward', { width: '14px' })}</span>
              Back
            </>
          ) : (
            <>
              Exit
              {icons('sign-out', { width: '14px' })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Header;
